import { Listenable } from "../listenable";

class IndexedDBService extends Listenable {
    constructor({ name, version = 1, stores = [] }) {
        super();

        this.name = name;
        this.version = version;
        this.stores = stores;
        this.db = null;
    }
    
    _init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.name, this.version);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                this._applyMigrations(db);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this);
            };

            request.onerror = () => reject(request.error);
        });
    }

    _applyMigrations(db) {
        this.stores
            .filter(store => !db.objectStoreNames.contains(store.name))
            .forEach(store => {
                const objectStore = db.createObjectStore(store.name, {
                    keyPath: store.keyPath || "id",
                    autoIncrement: store.autoIncrement ?? true,
                });

                if (store.indexes) {
                    store.indexes.forEach(idx => {
                        objectStore.createIndex(idx.name, idx.keyPath, {
                            unique: idx.unique || false
                        });
                    });
                }
            });
    }

    _storeRequest(storeName, method, payload, mode = 'readwrite') {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(storeName, mode);
            const store = transaction.objectStore(storeName);

            const request = payload !== undefined
                ? store[method](payload)
                : store[method]();

            request.onerror = () => reject({method, payload, error: request.error});
            request.onsuccess = () => {
                const result = request.result;
                const eventTrigger = {
                    'add': () => this.emit(`${storeName}:added`, { key: result, value: payload }),
                    'put': () => this.emit(`${storeName}:updated`, { key: result, value: payload }),
                    'delete': () => this.emit(`${storeName}:deleted`, { key: result, value: payload }),
                    'clear': () => this.emit(`${storeName}:cleared`),
                };

                if (method in eventTrigger) {
                    eventTrigger[method]();
                }

                resolve(request.result)
            };
        });
    }
}

const Database = new Proxy(IndexedDBService, {
    async construct(Target, args) {
        const instance = new Target(...args);
        
        await instance._init();

        return new Proxy(instance, {
            get(target, prop, receiver) {
                if (prop in target) {
                    return Reflect.get(target, prop, receiver);
                }

                if (!target.db.objectStoreNames.contains(prop)) {
                    return undefined;
                }

                const storeName = prop;
                return {
                    put: (data) => target._storeRequest(storeName, "put", data),
                    get: (key) => target._storeRequest(storeName, "get", key),
                    delete: (key) => target._storeRequest(storeName, "delete", key),
                    getAll: () => target._storeRequest(storeName, "getAll"),
                    clear: () => target._storeRequest(storeName, "clear"),
                    on: (event, cb) => target.on(`${storeName}:${event}`, cb),
                    count: () => target._storeRequest(storeName, "count", null, 'readonly'),
                    openKeyCursor: () => target._storeRequest(storeName, "openKeyCursor", null, 'readonly'),
                    index: (index) => target._storeRequest(storeName, "index", index),
                };
            }
        });
    }
});

export default Database;